import emojiRegex from 'emoji-regex';
import {
  QUIZ_SLOTS_PER_ROW,
  QUIZ_WINDOW_MS,
  type QuizBoardDto,
  type QuizGuessErrorCode,
  type QuizMark,
  type QuizPick,
  type QuizStatus,
} from '../../api/quizTypes';

/**
 * AOTD emoji quiz, the view side. The board comes from
 * `GET /art-of-the-day/quiz` (artOfTheDayApi.getQuiz) and the race socket;
 * the server scores and ranks (modules/aotd/quiz/CONTRACT.md in the backend).
 *
 * A board is one row per author: three emoji slots (closed past the number
 * of emojis that author set) and a fourth slot for the author's signature
 * emoji (closed when they have none). A normal day has one row; a stolen
 * AOTD has two. Picks are kept flat, four per row.
 */

export const SLOTS_PER_ROW = QUIZ_SLOTS_PER_ROW;
/** The guessing's element id, so the art card's "?" box can scroll down to it. */
export const QUIZ_ANCHOR_ID = 'aotd-guessing';
export const QUIZ_LENGTH_MS = QUIZ_WINDOW_MS;

/** The points, for the legend. The server does the scoring. */
export const SCORE = { exact: 3, misplaced: 1, wrong: -2 } as const;

export type Mark = QuizMark;

export interface QuizRow {
  author: string;
  /** How many emojis this author set (1-3). Slots past it are closed. */
  emojiCount: number;
  /** False when the author has no signature emoji: the yellow slot is closed. */
  hasSignature: boolean;
  /** Emojis this author burned on earlier AOTDs: never in today's answer. */
  usedEmojis: string[];
}

export interface QuizBoard {
  status: QuizStatus;
  rows: QuizRow[];
  /** Flat, four per row; null on closed slots. Null while the quiz runs. */
  answer: QuizPick[] | null;
}

export interface Ranked {
  id: string;
  username: string;
  /** Lock time, ms after the quiz opened. */
  atMs: number;
  picks: QuizPick[];
  isAuthor: boolean;
  isYou: boolean;
  attempt: number;
  /** 2 when this player has locked twice (Second Guess). */
  attemptOf: number;
  // after close only
  marks?: Mark[];
  score?: number;
  perfect?: boolean;
  rank: number | null;
  /** A player's weaker guess when they had two: shown faded, never ranked. */
  superseded: boolean;
}

export function isOpenSlot(rows: QuizRow[], i: number): boolean {
  const row = rows[Math.floor(i / SLOTS_PER_ROW)];
  const pos = i % SLOTS_PER_ROW;
  if (!row) return false;
  return pos === SLOTS_PER_ROW - 1 ? row.hasSignature : pos < row.emojiCount;
}

export function isAuthorSlot(i: number): boolean {
  return i % SLOTS_PER_ROW === SLOTS_PER_ROW - 1;
}

export function boardView(board: QuizBoardDto): QuizBoard {
  return {
    status: board.status,
    rows: board.rows.map((r) => ({
      author: r.username,
      emojiCount: r.emojiCount,
      hasSignature: r.hasSignature,
      usedEmojis: r.usedEmojis ?? [],
    })),
    answer: board.answer,
  };
}

/**
 * The guesses as the line and the table draw them. While the quiz runs: the
 * feed, oldest lock first. After close: ranked players, then superseded
 * guesses, then the authors, the order the leaderboard reads in.
 */
export function guessViews(board: QuizBoardDto): Ranked[] {
  const locks = new Map<string, number>();
  board.guesses.forEach((g) => locks.set(g.username, (locks.get(g.username) ?? 0) + 1));
  const views: Ranked[] = board.guesses.map((g) => ({
    id: g.id,
    username: g.username,
    atMs: g.atMs,
    picks: g.picks,
    isAuthor: g.isAuthor,
    isYou: g.isYou,
    attempt: g.attempt,
    attemptOf: (locks.get(g.username) ?? 1) > 1 ? 2 : 1,
    marks: g.marks,
    score: g.score,
    perfect: g.perfect,
    rank: g.rank ?? null,
    superseded: Boolean(g.superseded),
  }));
  if (board.status !== 'closed') return views;
  const players = views
    .filter((g) => !g.isAuthor && !g.superseded)
    .sort((a, b) => (a.rank ?? Infinity) - (b.rank ?? Infinity) || a.atMs - b.atMs);
  return [...players, ...views.filter((g) => !g.isAuthor && g.superseded), ...views.filter((g) => g.isAuthor)];
}

/** The picks the box starts from: your latest lock, else a heist draft, else blanks. */
export function initialPicks(board: QuizBoardDto): QuizPick[] {
  const size = board.rows.length * SLOTS_PER_ROW;
  const mine = board.guesses.filter((g) => g.isYou).sort((a, b) => b.attempt - a.attempt)[0];
  const from = mine?.picks ?? board.you.draft ?? null;
  return Array.from({ length: size }, (_, i) => (from && typeof from[i] === 'string' ? from[i] : null));
}

/**
 * When your last guess locked (ms into the quiz), once you can't lock again:
 * no attempts left, or time ran out before the close task ran (the server
 * then answers `reason: "closed"` even while the board is still open).
 */
export function lastLock(board: QuizBoardDto): number | null {
  if (board.you.canGuess) return null;
  const mine = board.guesses.filter((g) => g.isYou).sort((a, b) => b.attempt - a.attempt)[0];
  return mine ? mine.atMs : null;
}

/**
 * Whether the viewer gets the guess box while the board is open. A player
 * keeps it after locking (it shows the locked picks) and after time ran out,
 * which the server reports as `reason: "closed"` until the close task flips
 * the status.
 */
export function playsQuiz(you: QuizBoardDto['you']): boolean {
  return you.eligible && (you.canGuess || you.reason === 'no_attempts_left' || you.reason === 'closed');
}

/**
 * The quiz the server answers with is "today's". The page can still show the
 * previous AOTD (before the next unlock), so the board only counts when one of
 * its authors made the art on the page.
 */
export function belongsToArt(board: QuizBoardDto, author: string | null | undefined): boolean {
  return !!author && board.rows.some((r) => r.username === author);
}

/** One emoji, or empty. Mirrors the staging box's check. */
export function singleEmoji(raw: string): string | null {
  const t = raw.trim();
  if (!t) return null;
  const found = t.match(emojiRegex());
  return found && found.length === 1 && found[0] === t ? t : null;
}

export function formatClock(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

export function formatMinutes(ms: number): string {
  const safe = Math.max(0, ms);
  const m = Math.floor(safe / 60000);
  const s = Math.floor((safe % 60000) / 1000);
  return `${m}:${String(s).padStart(2, '0')}`;
}

const GUESS_ERROR_COPY: Record<QuizGuessErrorCode, string> = {
  auth_required: 'Sign in to play.',
  no_quiz: "There's no guessing right now.",
  day_mismatch: "That guessing is over. Refresh for today's.",
  quiz_closed: 'Too late, the guessing just closed.',
  not_eligible: 'Only members can guess.',
  no_attempts_left: 'Your guess is already locked.',
  bad_picks: 'One emoji per box.',
  empty_guess: 'Put at least one emoji in first.',
  closed_slot: 'That box is closed today.',
  used_emoji: "One of those was used in an earlier AOTD, so it can't be in this one.",
  duplicate_pick: 'The same emoji is in two boxes.',
  board_changed: 'The art was just stolen. Lock again on the new board.',
  rate_limited: 'Slow down a little, then lock again.',
};

/** The refusal code of a failed lock, or null for a network or unknown error. */
export function guessErrorCode(error: unknown): QuizGuessErrorCode | null {
  const data =
    error && typeof error === 'object' && 'data' in error ? (error as { data?: unknown }).data : undefined;
  const code =
    data && typeof data === 'object' && 'code' in data ? (data as { code?: unknown }).code : undefined;
  return typeof code === 'string' && code in GUESS_ERROR_COPY ? (code as QuizGuessErrorCode) : null;
}

/** What to say under the box when a lock is refused. */
export function guessErrorText(error: unknown): string {
  const code = guessErrorCode(error);
  return code ? GUESS_ERROR_COPY[code] : "Couldn't lock it. Try again.";
}

/**
 * Refusals that mean the board on screen is out of date (the quiz closed, the
 * day moved on, the guess already landed from another tab, a heist is
 * redrawing the art). The board is read
 * again at once rather than waiting for the next poll or a socket frame.
 */
const STALE_BOARD_CODES: ReadonlySet<QuizGuessErrorCode> = new Set<QuizGuessErrorCode>([
  'no_quiz',
  'day_mismatch',
  'quiz_closed',
  'not_eligible',
  'no_attempts_left',
  'board_changed',
]);

export function boardIsStale(error: unknown): boolean {
  const code = guessErrorCode(error);
  return code !== null && STALE_BOARD_CODES.has(code);
}
